import type { DictationAdapter } from "@assistant-ui/react";
import {
  CredentialCache,
  type DictationCredentials,
  type DictationTransport,
  LiveDictationSession,
  type TransportEvents,
} from "./liveDictation";
import { TARGET_SAMPLE_RATE } from "./micCapture";

export interface DeepgramDictationAdapterOptions {
  baseUrl: string;
  getHeaders: () => Record<string, string>;
  model?: string;
  language?: string;
}

// Tokens live 30 s and only need to be valid when the socket opens; leave margin for the handshake.
const TOKEN_REUSE_MS = 20 * 1000;
// Deepgram closes a socket after 10 s with no audio, e.g. while the mic permission prompt is up.
const KEEP_ALIVE_MS = 5000;
// Deepgram answers a Finalize in a few hundred ms; don't hold the composer longer than this.
const FINALIZE_TIMEOUT_MS = 1500;

/**
 * Live dictation over Deepgram's streaming WebSocket. Audio stays under our BAA.
 */
export class DeepgramDictationAdapter implements DictationAdapter {
  disableInputDuringDictation = false;

  private credentials: CredentialCache;
  private query: string;

  constructor(options: DeepgramDictationAdapterOptions) {
    this.credentials = new CredentialCache(async () => {
      const res = await fetch(`${options.baseUrl}/superdoc/dictation/token/`, {
        method: "POST",
        headers: options.getHeaders(),
      });
      if (!res.ok) throw new Error(`Dictation token: ${res.status}`);
      return (await res.json()) as DictationCredentials;
    }, TOKEN_REUSE_MS);
    this.query = new URLSearchParams({
      model: options.model ?? "nova-3-medical",
      language: options.language ?? "en",
      encoding: "linear16",
      sample_rate: String(TARGET_SAMPLE_RATE),
      channels: "1",
      smart_format: "true",
      interim_results: "true",
      endpointing: "300",
    }).toString();
  }

  /** Warms a token when the user reaches for the mic, so the click streams immediately. */
  prefetch() {
    this.credentials.prefetch();
  }

  listen(): DictationAdapter.Session {
    return new LiveDictationSession(new DeepgramTransport(this.query), this.credentials.take());
  }
}

interface DeepgramResults {
  type?: string;
  is_final?: boolean;
  from_finalize?: boolean;
  channel?: { alternatives?: Array<{ transcript?: string }> };
}

class DeepgramTransport implements DictationTransport {
  private ws: WebSocket | null = null;
  private finalized: (() => void) | null = null;
  private keepAlive: ReturnType<typeof setInterval> | undefined;

  constructor(private query: string) {}

  open({ token, url }: DictationCredentials, events: TransportEvents): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${url}?${this.query}`, ["bearer", token]);
      this.ws = ws;
      ws.binaryType = "arraybuffer";
      let opened = false;
      ws.onopen = () => {
        opened = true;
        this.keepAlive = setInterval(() => ws.send(JSON.stringify({ type: "KeepAlive" })), KEEP_ALIVE_MS);
        resolve();
      };
      ws.onerror = () => reject(new Error("Deepgram socket error"));
      ws.onclose = () => {
        if (opened && this.ws === ws) events.onError();
      };
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data as string) as DeepgramResults;
        if (msg.type !== "Results") return;
        const transcript = msg.channel?.alternatives?.[0]?.transcript ?? "";
        if (msg.is_final) events.onFinal(transcript);
        else if (transcript) events.onInterim(transcript);
        if (msg.from_finalize) this.finalized?.();
      };
    });
  }

  send(frame: ArrayBuffer) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(frame);
  }

  async finish() {
    const ws = this.ws;
    if (ws?.readyState !== WebSocket.OPEN) return;
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, FINALIZE_TIMEOUT_MS);
      this.finalized = () => {
        clearTimeout(timer);
        resolve();
      };
      ws.send(JSON.stringify({ type: "Finalize" }));
    });
  }

  close() {
    clearInterval(this.keepAlive);
    const ws = this.ws;
    this.ws = null;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "CloseStream" }));
    ws?.close();
  }
}
